import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  IconButton,
  Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useDispatch } from 'react-redux';
import {
  createFlashcardType,
  updateFlashcardType,
  deleteFlashcardType,
} from '../../actions/flashcardTypes';

const ROLES = ['question', 'solution'];
const QUESTION_TYPES = ['text', 'image', 'sound'];
const SOLUTION_TYPES = ['text', 'code', 'date', 'enumeration', 'quiz', 'dropdown', 'imagepos'];

const fieldTypesForRole = (role) =>
  role === 'solution' ? SOLUTION_TYPES : QUESTION_TYPES;

const DEFAULTABLE_TYPES = ['text', 'quiz'];

const generateKey = () =>
  `f_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

const emptyField = () => ({
  key: generateKey(),
  label: '',
  role: 'question',
  fieldType: 'text',
  defaultValue: '',
});

const useStyles = makeStyles((theme) => ({
  buttonSubmit: { margin: 10 },
  buttonClear: { margin: 10 },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: '1px solid #ddd',
    borderRadius: 4,
    transition: 'background-color 0.15s, opacity 0.15s',
  },
  fieldRowDragging: {
    opacity: 0.4,
    background: '#e3f2fd',
  },
  fieldRowOver: {
    borderTop: '3px solid #1976d2',
  },
  dragHandle: {
    cursor: 'grab',
    color: '#999',
    display: 'flex',
    alignItems: 'center',
    '&:active': { cursor: 'grabbing' },
  },
}));

const QuizDefaultEditor = ({ value, onChange }) => {
  let options = [];
  try {
    options = JSON.parse(value || '[]');
  } catch {
    options = [];
  }
  if (!Array.isArray(options)) options = [];

  const update = (newOptions) => onChange(JSON.stringify(newOptions));

  return (
    <div
      style={{
        marginLeft: 40,
        marginTop: 4,
        padding: 8,
        border: '1px solid #ddd',
        borderRadius: 4,
        background: '#fafafa',
      }}
    >
      <div style={{ fontSize: '0.85em', color: '#888', marginBottom: 4 }}>
        Default quiz options (check = correct)
      </div>
      {options.map((opt, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          <Checkbox
            checked={!!opt.correct}
            size="small"
            onChange={(e) => {
              const updated = options.map((o, i) =>
                i === idx ? { ...o, correct: e.target.checked } : o,
              );
              update(updated);
            }}
          />
          <TextField
            value={opt.text || ''}
            onChange={(e) => {
              const updated = options.map((o, i) =>
                i === idx ? { ...o, text: e.target.value } : o,
              );
              update(updated);
            }}
            size="small"
            variant="outlined"
            style={{ flex: 1 }}
            placeholder={`Option ${idx + 1}`}
          />
          <IconButton
            size="small"
            onClick={() => {
              const updated = options.filter((_, i) => i !== idx);
              update(updated);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button
        size="small"
        startIcon={<AddCircleIcon />}
        onClick={() => update([...options, { text: '', correct: false }])}
      >
        Add option
      </Button>
    </div>
  );
};

const DropdownDefaultEditor = ({ value, onChange }) => {
  const containerRef = useRef(null);
  let options = [];
  try {
    options = JSON.parse(value || '[]');
  } catch {
    options = [];
  }
  if (!Array.isArray(options)) options = [];
  if (!options.length) options = [{ text: '', correct: true }];

  const normalizeSingleCorrect = (newOptions) => {
    const firstCorrectIdx = newOptions.findIndex((opt) => !!opt.correct);
    return newOptions.map((opt, idx) => ({
      ...opt,
      correct: firstCorrectIdx >= 0 ? idx === firstCorrectIdx : idx === 0,
    }));
  };

  const update = (newOptions) =>
    onChange(JSON.stringify(normalizeSingleCorrect(newOptions)));

  const focusDropdownItem = (itemIdx) => {
    setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const inputs = container.querySelectorAll('input, textarea');
      if (inputs[itemIdx]) inputs[itemIdx].focus();
    }, 0);
  };

  return (
    <div
      ref={containerRef}
      style={{
        marginLeft: 40,
        marginTop: 4,
        padding: 8,
        border: '1px solid #ddd',
        borderRadius: 4,
        background: '#fafafa',
      }}
    >
      <div style={{ fontSize: '0.85em', color: '#888', marginBottom: 4 }}>
        Default dropdown options (exactly one correct)
      </div>
      {options.map((opt, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          <Checkbox
            checked={!!opt.correct}
            size="small"
            onChange={() => {
              const updated = options.map((o, i) => ({
                ...o,
                correct: i === idx,
              }));
              update(updated);
            }}
          />
          <TextField
            value={opt.text || ''}
            onChange={(e) => {
              const updated = options.map((o, i) =>
                i === idx ? { ...o, text: e.target.value } : o,
              );
              update(updated);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                e.preventDefault();
                const newOptions = [...options];
                newOptions.splice(idx + 1, 0, { text: '', correct: false });
                update(newOptions);
                focusDropdownItem(idx + 1);
              }
            }}
            size="small"
            variant="outlined"
            style={{ flex: 1 }}
            placeholder={`Option ${idx + 1}`}
          />
          <IconButton
            size="small"
            onClick={() => {
              const updated = options.filter((_, i) => i !== idx);
              update(updated.length ? updated : [{ text: '', correct: true }]);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button
        size="small"
        startIcon={<AddCircleIcon />}
        onClick={() => update([...options, { text: '', correct: false }])}
      >
        Add option
      </Button>
    </div>
  );
};

const CardTypeForm = ({ cardType, currentId, close }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: '',
    owner: '',
    fields: [emptyField()],
  });

  useEffect(() => {
    if (cardType && currentId !== 0) {
      setFormData({
        name: cardType.name || '',
        owner: cardType.owner || '',
        fields:
          cardType.fields && cardType.fields.length > 0
            ? cardType.fields.map((f) => ({ ...f, key: f.key || generateKey() }))
            : [emptyField()],
      });
    }
  }, [cardType, currentId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentId === 0) {
      dispatch(createFlashcardType(formData));
    } else if (cardType && cardType._id) {
      dispatch(updateFlashcardType(cardType._id, formData));
    }
    close();
  };

  const addField = () => {
    setFormData({
      ...formData,
      fields: [...formData.fields, emptyField()],
    });
  };

  const removeField = (index) => {
    const fields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields });
  };

  const updateField = (index, key, value) => {
    const fields = formData.fields.map((f, i) =>
      i === index ? { ...f, [key]: value } : f,
    );
    setFormData({ ...formData, fields });
  };

  const dragSrcIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => (e) => {
    dragSrcIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (targetIndex) => (e) => {
    e.preventDefault();
    const srcIndex = dragSrcIndex.current;
    if (srcIndex === null || srcIndex === targetIndex) {
      dragSrcIndex.current = null;
      setDragOverIndex(null);
      return;
    }
    const fields = [...formData.fields];
    const [moved] = fields.splice(srcIndex, 1);
    fields.splice(targetIndex, 0, moved);
    setFormData({ ...formData, fields });
    dragSrcIndex.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragSrcIndex.current = null;
    setDragOverIndex(null);
  };

  const clear = () => {
    setFormData({ name: '', owner: '', fields: [emptyField()] });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>
        {currentId === 0 ? 'Create' : 'Update'} card type:
      </DialogTitle>
      <div style={{ margin: 16 }}>
        <TextField
          label="Type name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
          autoFocus
          style={{ marginBottom: 16 }}
        />

        <h4 style={{ marginBottom: 8 }}>Fields</h4>
        {formData.fields.map((field, index) => (
          <div
            key={index}
            style={{ marginBottom: 8 }}
            draggable
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver(index)}
            onDrop={handleDrop(index)}
            onDragEnd={handleDragEnd}
          >
            <div
              className={`${classes.fieldRow} ${
                dragSrcIndex.current === index ? classes.fieldRowDragging : ''
              } ${dragOverIndex === index && dragSrcIndex.current !== index ? classes.fieldRowOver : ''}`}
              style={{ marginBottom: 0 }}
            >
              <span className={classes.dragHandle} title="Drag to reorder">
                <DragIndicatorIcon fontSize="small" />
              </span>
              <span style={{ minWidth: 24, textAlign: 'right', color: '#888' }}>
                {index + 1}.
              </span>
              <TextField
                label="Label"
                value={field.label}
                onChange={(e) => updateField(index, 'label', e.target.value)}
                style={{ flex: 1 }}
              />
              <Select
                value={field.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  const allowed = fieldTypesForRole(newRole);
                  const fields = formData.fields.map((f, i) => {
                    if (i !== index) return f;
                    const correctedType = allowed.includes(f.fieldType)
                      ? f.fieldType
                      : allowed[0];
                    return { ...f, role: newRole, fieldType: correctedType };
                  });
                  setFormData({ ...formData, fields });
                }}
                style={{ minWidth: 110 }}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
              <Select
                value={field.fieldType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const shouldKeepDefaultValue = DEFAULTABLE_TYPES.includes(nextType) || nextType === 'dropdown';
                  const fields = formData.fields.map((f, i) => {
                    if (i !== index) return f;
                    return {
                      ...f,
                      fieldType: nextType,
                      defaultValue: shouldKeepDefaultValue ? (f.defaultValue || '') : '',
                    };
                  });
                  setFormData({ ...formData, fields });
                }}
                style={{ minWidth: 100 }}
              >
                {fieldTypesForRole(field.role).map((ft) => (
                  <MenuItem key={ft} value={ft}>
                    {ft}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                size="small"
                onClick={() => removeField(index)}
                disabled={formData.fields.length <= 1}
                aria-label="Remove field"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
            {field.fieldType === 'text' && field.role === 'question' && (
              <div style={{ marginLeft: 40, marginTop: 4 }}>
                <TextField
                  label="Default value"
                  value={field.defaultValue || ''}
                  onChange={(e) => updateField(index, 'defaultValue', e.target.value)}
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  variant="outlined"
                />
              </div>
            )}
            {field.fieldType === 'quiz' && (
              <QuizDefaultEditor
                value={field.defaultValue || ''}
                onChange={(val) => updateField(index, 'defaultValue', val)}
              />
            )}
            {field.fieldType === 'dropdown' && (
              <DropdownDefaultEditor
                value={field.defaultValue || ''}
                onChange={(val) => updateField(index, 'defaultValue', val)}
              />
            )}
          </div>
        ))}

        <Button
          startIcon={<AddCircleIcon />}
          onClick={addField}
          size="small"
          style={{ marginBottom: 16 }}
        >
          Add field
        </Button>

        <Button
          className={classes.buttonSubmit}
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleSubmit}
        >
          {currentId === 0 ? 'Submit' : 'Update'}
        </Button>
        <Button
          onClick={clear}
          className={classes.buttonClear}
          variant="contained"
          color="secondary"
          size="small"
          fullWidth
        >
          Clear
        </Button>
        {currentId !== 0 && cardType && (
          <Button
            onClick={() => {
              dispatch(deleteFlashcardType(cardType._id));
              close();
            }}
            className={classes.buttonClear}
            variant="contained"
            color="secondary"
            size="small"
            fullWidth
          >
            Delete
          </Button>
        )}
        <Button
          onClick={close}
          className={classes.buttonClear}
          variant="contained"
          color="secondary"
          size="small"
          fullWidth
        >
          Close
        </Button>
      </div>
    </Dialog>
  );
};

export default CardTypeForm;
